import { Container } from "aurelia-dependency-injection";
import { computed, defineComponent, PropType } from "vue";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import {
    AdministrativePermissionEnum,
    ApplicationEnum,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../../../../../enums/enums";
import { ToastrService } from "../../../../../../services/toastr.service";
import { UserService } from "../../../../../../services/user-service";
import { Hospitalization } from "../../../../../../models/pal-health-history/hospitalization";
import { ValidationController } from "aurelia-validation";
import { formatName } from "../../../../../../common/format-name";
import { PermissionManager } from "../../../../../../common/utilities/permission-manager";
import { IEnumResponse } from "../../../../../../interfaces/i-enum";
import { ITypeaheadOptions } from "../../../../../../interfaces/i-typeahead";
import { IUserResult, IGetUsersByRoleQuery } from "../../../../../../interfaces/i-user";
import { IPromptOptions } from "../../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { VendorsService } from "../../../../../../services/vendor-service";
import {
    IGetVendorByTermQuery,
    IVendorByIdRequestPayload
} from "../../../../../../resources-vue/vue-interfaces/i-vendor";
import { IGetPatientsSlimInfoResult } from "../../../../../../resources-vue/vue-interfaces/i-patient";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";

import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import Prompt from "../../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import DiagnosisTable from "../DiagnosisTable/DiagnosisTable.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        TypeaheadInput,
        DiagnosisTable
    },

    props: {
        accountId: {
            type: String
        },
        application: {
            type: Number
        },
        cancelHospitalization: {
            type: Function
        },
        closeViewDetailsWindow: {
            type: Function
        },
        saveHospitalization: {
            type: Function
        },
        hospitalization: {
            type: Hospitalization,
            default: new Hospitalization()
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        isNew: {
            type: Boolean,
            default: false
        },
        providerId: {
            type: String,
            default: ""
        },
        patientInfo: {
            type: Object as PropType<IGetPatientsSlimInfoResult>,
            default: null
        },
        isViewDetailsRequested: {
            type: Boolean,
            default: false
        },
        roles: {
            type: Array as PropType<IEnumResponse[]>,
            default: []
        }
    },

    validations() {
        return {
            hospitalization: {
                AdmissionDate: { required }
            }
        };
    },

    data(vm) {
        return {
            _toastrService: null,
            _usersService: null as UserService,
            _vendorsService: null as VendorsService,
            _permissionManager: null as PermissionManager,
            dialogRef: null,
            diagnosisLimit: 7 as number,
            ignoreFacilityChange: false as boolean,
            hasData: false as boolean,
            cachedData: null as Hospitalization,
            validationController: null as ValidationController,
            maxLength: 5000 as number,
            canChangeStatus: false as boolean,
            isDiagnosisUnknown: false as boolean,
            selectedHospitalizedFacility: null as ITypeaheadOptions,
            selectedCommunityLiaison: null as ITypeaheadOptions,
            v$: useVuelidate(),
            formatDateInput
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    watch: {
        isDiagnosisUnknown() {
            this.diagnosisUnknownChanged();
        },
        birthWeightLarge(newValue) {
            this.selectedHospitalizedFacilityChanged(newValue);
        }
    },

    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._usersService = Container.instance.get(UserService);
        this._vendorsService = Container.instance.get(VendorsService);
        this._permissionManager = Container.instance.get(PermissionManager);
    },

    async mounted() {
        this.cachedData = new Hospitalization(this.hospitalization);
        this.initPermission();
        this.checkHasData();
        if (!this.isNew) {
            this.isDiagnosisUnknown = this.hospitalization.HospitalizationHistoryDiagnoses.length < 1;
            this.initValues();
        }
    },

    methods: {
        handleSelectedFacilityChange(newValue: any) {
            this.selectedHospitalizedFacility = newValue;
            this.selectedHospitalizedFacilityChanged(newValue);
        },

        initValues() {
            if (!!this.hospitalization?.CommunityLiaisonId) {
                this.selectedCommunityLiaison = {
                    name: `${formatName(
                        this.hospitalization.CommunityLiaisonFirstName,
                        this.hospitalization.CommunityLiaisonLastName,
                        "FN LN"
                    )}`,
                    value: this.hospitalization.CommunityLiaisonId
                };
            }
            if (!!this.hospitalization?.FacilitySourceId) {
                this.ignoreFacilityChange = true;
                this.selectedHospitalizedFacility = {
                    name: this.hospitalization.FacilitySourceName,
                    value: this.hospitalization.FacilitySourceId
                };
            }
        },

        initPermission() {
            this.canChangeStatus = this._permissionManager.checkPermission(
                AdministrativePermissionEnum.ChangePatientStatus,
                PermissionActionEnum.canEdit
            );
        },

        checkHasData() {
            return (
                !!this.hospitalization.AdmissionDate ||
                !!this.hospitalization.DischargeDate ||
                !!this.hospitalization.Comments ||
                !!this.hospitalization.DischargedTo ||
                this.hospitalization.HospitalizationHistoryDiagnoses.length > 0
            );
        },

        cancel() {
            this.cancelHospitalization();
        },

        close() {
            this.closeViewDetailsWindow();
        },

        diagnosisUnknownChanged() {
            if (this.isDiagnosisUnknown) {
                this.hospitalization.HospitalizationHistoryDiagnoses = [];
                this.diagnosisLimit = 0;
            } else {
                this.diagnosisLimit = 7;
            }
        },

        async selectedHospitalizedFacilityChanged(facility: ITypeaheadOptions) {
            if (!facility) {
                return;
            }
            this.hospitalization.FacilitySourceId = facility.value;
            this.hospitalization.FacilitySourceName = facility.name;
            if (!facility.value || this.ignoreFacilityChange) {
                this.ignoreFacilityChange = false;
                return;
            }
            const payload: IVendorByIdRequestPayload = {
                id: facility.value,
                accountId: this.accountId,
                application: this.application
            };
            let selectedFacility = await this._vendorsService.getVendorById(payload);
            this.selectedCommunityLiaison = {
                name: selectedFacility.communityLiaisonName,
                value: selectedFacility.communityLiaisonId
            };
        },

        handleSelectedCommunityLiaison(newValue: any) {
            this.selectedCommunityLiaison = newValue;
            this.selectedCommunityLiaisonChanged(newValue);
        },

        selectedCommunityLiaisonChanged(communityL: IUserResult): void {
            if (communityL) {
                this.hospitalization.CommunityLiaisonId = communityL.value as string;
            } else {
                this.hospitalization.CommunityLiaisonId = null;
            }
        },

        async save() {
            const isValidForm = await this.v$.$validate();
            if (isValidForm) {
                if (!this.checkHasData()) {
                    return;
                }
                if (!this.isDiagnosisUnknown && !this.hospitalization?.HospitalizationHistoryDiagnoses?.length) {
                    this._toastrService.error({
                        title: "Error - Hospitalizations",
                        message: "Please add a diagnosis or select Unknown."
                    });
                    return;
                }
                let isChangeStatus: boolean = false;
                if (
                    this.isNew &&
                    !this.patientInfo.isHospitalized &&
                    !!this.hospitalization.AdmissionDate &&
                    !this.hospitalization.DischargeDate
                ) {
                    if (this.isNew && !this.patientInfo?.isHospitalized && this.canChangeStatus) {
                        let promptOptions: IPromptOptions = {
                            message: "Do you want to change the patient's status to Hospitalized?",
                            okText: "Yes",
                            cancelText: "No"
                        };
                        this.dialogRef = this.$dialog.open(Prompt, {
                            props: {
                                modal: true,
                                contentStyle: {
                                    overflowX: "hidden"
                                },
                                showHeader: false
                            },
                            data: {
                                promptOptions
                            },
                            onClose: (options) => {
                                if (options.data === "success") {
                                    isChangeStatus = true;
                                }
                            }
                        });
                    }
                }
                await this.saveHospitalization(isChangeStatus);
                await this.requestServiceLocationChange();
            } else {
                this._toastrService.error({
                    title: "Error - Hospital Admission Form",
                    message: "Please fill required fields"
                });
            }
        },

        async requestServiceLocationChange() {
            let promptOptions: IPromptOptions = {
                message: "Do you want to update the service location for this patient?",
                okText: "Yes",
                cancelText: "No"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data !== "success") {
                        return;
                    }
                }
            });
            // if (!!this.patientInfo) {
            // let redirectUrl = `/patients/palliative-patient-edit/${this.patientInfo.id}/demographics`;
            // this.$router.push(redirectUrl);
            // }
        },

        async getUsersByRole(filter: string, limit: number) {
            let users = await this._usersService.getUsersByRole(
                this.getUserQuery("Community Liaison Officer", true, filter)
            );
            return users.map((user) => ({
                name: `${formatName(user.firstName, user.lastName, "FN LN")}`,
                value: user.id
            }));
        },

        getUserQuery(userRole: string, include: boolean, filter: string): IGetUsersByRoleQuery {
            let roleCode = this.roles.filter((role: IEnumResponse) => role.name === userRole)[0].value;
            return {
                role: [roleCode] as number[],
                include: include,
                term: filter,
                providerIds: [this.providerId],
                application: this.application
            };
        },

        async getVendorList(filter: string, limit: number) {
            const payload: IGetVendorByTermQuery = {
                accountId: this.accountId,
                application: this.application,
                name: filter,
                providerIds: [this.providerId]
            };
            let facilityList = await this._vendorsService.getVendorByTerm(payload);
            return facilityList.map((facility) => ({
                name: facility.name,
                value: facility.id
            }));
        },
        handleDateChange(date: any, name: string) {
            if (name === "hospitalization-discharge-date") {
                this.hospitalization.DischargeDate = date;
            }
            if (name === "hospitalization-admission-date") {
                this.hospitalization.AdmissionDate = date;
            }
        },
        handleDateInputChange(e: any) {
            const name = e.target.name;
            const value = e.target.value;
            if (name === "hospitalization-discharge-date") {
                this.hospitalization.DischargeDate = value;
            }
            if (name === "hospitalization-admission-date") {
                this.hospitalization.AdmissionDate = value;
            }
        },
        handleUpdateDiagnosis(newDiagnosis: any) {
            this.hospitalization.HospitalizationHistoryDiagnoses = newDiagnosis;
        }
    }
});
